import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import UnifiedLoader from '@/components/ui/UnifiedLoader';
import DelayedLoader from '@/components/ui/DelayedLoader';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * ProtectedRoute - Wraps routes that require authentication
 * 
 * Features:
 * - Shows loading state while checking auth (with 500ms delay to avoid flash)
 * - Redirects to auth page if not authenticated
 * - Preserves intended destination in redirect URL
 * - Consistent behavior across all protected routes
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  redirectTo = '/auth' 
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Use DelayedLoader to avoid "flash" on fast auth checks
    // Only shows loading screen if auth takes > 500ms (cold start)
    return (
      <DelayedLoader delay={500}>
        <UnifiedLoader mode="fullscreen" text="Loading" />
      </DelayedLoader>
    );
  }

  if (!user) {
    // Preserve intended destination in redirect URL
    const currentPath = location.pathname + location.search;
    const redirect = encodeURIComponent(currentPath);
    return <Navigate to={`${redirectTo}?redirect=${redirect}`} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
